import React from 'react'
import { Link } from 'react-router-dom'
import style from './style.module.scss'
import { Text, Header as DesignHeader } from '@crossfit/design-system'
import { Role, User } from 'types'
import classnames from 'classnames'
import { Icon } from '@crossfit/ui'
import { useEffect, useState } from 'react'
import { useQuery } from 'react-query'
import * as userService from 'services/user'
import { AccountButton } from 'components/AccountButton'
import Select, { StylesConfig } from 'react-select'
import { useMediaQuery } from 'hooks/useMediaQuery'
import ExternalLink from './external-link.svg'
import AccountMenu from 'components/AccountMenu'
import classNames from 'classnames'
import logoSmall from './Logos/logoSmall.svg'
import logoMedium from './Logos/logoMedium.svg'
import { useDispatch, useSelector } from 'react-redux'
import {
  getAffiliate,
  getBanner,
  getMyAffiliates,
  getUser,
} from 'store/selectors'
import { AppDispatch } from 'store'
import { selectAffiliateThunk } from 'store/slices/myAffiliates'
import {
  canAccessAffiliateStarterKit,
  canAccessContentKit,
  canAccessDashboard,
  canAccessPartnerNetwork,
  canAccessPlaybook,
  canAccessProgramming,
  canAccessRoundtables,
} from 'components/Auth/abilities'
import { useMyActiveSubscriptions } from 'hooks/subscriptions'
import useBanners from 'hooks/useBanner'
import NotificationBanner from 'components/NotificationBanner'

type ResourcesMenuProps = {
  children: React.ReactNode
}

export function ResourcesMenu({ children }: ResourcesMenuProps) {
  const [isMenuExpanded, setIsMenuExpanded] = useState(false)
  const [isResourcesMenuExpanded, setIsResourcesMenuExpanded] = useState(false)
  const isMobile = useMediaQuery(`(max-width: 1183px)`)

  useEffect(() => {
    if (isResourcesMenuExpanded) {
      setIsMenuExpanded(false)
    }
  }, [isResourcesMenuExpanded])

  useEffect(() => {
    if (isMenuExpanded) {
      setIsResourcesMenuExpanded(false)
    }
  }, [isMenuExpanded])

  return (
    <div
      className={classnames(style.link, style.hasSubmenu)}
      onClick={() => setIsMenuExpanded((s) => !s)}
      aria-expanded={isMenuExpanded ? 'true' : 'false'}
    >
      <div className={classnames(style.navLink, style.sectionHasSubmenu)}>
        Business Resources
      </div>
      <div
        className={classnames(
          style.dropdownMenu,
          isResourcesMenuExpanded && style.isExpanded,
        )}
        onClick={() => isMobile && setIsResourcesMenuExpanded((s) => !s)}
      >
        {children}
      </div>
    </div>
  )
}

function NavigationMenu({ close }) {
  const subscriptions = useMyActiveSubscriptions()
  const { selectedAffiliate } = useSelector(getMyAffiliates)

  const [isBusinessOpen, setIsBusinessOpen] = useState(false)
  const bannerState = useBanners()
  const dashboardAccess = canAccessDashboard(selectedAffiliate, subscriptions)
  const programmingAccess = canAccessProgramming(
    selectedAffiliate,
    subscriptions,
  )
  const partnerNetworkAccess = canAccessPartnerNetwork(
    selectedAffiliate,
    subscriptions,
  )

  const roundtablesAccess = canAccessRoundtables(
    selectedAffiliate,
    subscriptions,
  )

  const playbookAccess = canAccessPlaybook(selectedAffiliate, subscriptions)

  const starterKitAccess = canAccessAffiliateStarterKit(
    selectedAffiliate,
    subscriptions,
  )

  const marketingAccess = canAccessContentKit(selectedAffiliate, subscriptions)

  return (
    <div
      className={classNames(
        style.navigationWrapper,
        bannerState?.showingBanner && style.showBanner,
      )}
    >
      <div className={style.navigationLinkList}>
        {dashboardAccess && (
          <Link to='/' className={style.navigationLink} onClick={() => close()}>
            <DesignHeader as={'h1'} size={'md'}>
              DASHBOARD
            </DesignHeader>
          </Link>
        )}
        {programmingAccess && (
          <Link
            to='/programming'
            className={style.navigationLink}
            onClick={() => close()}
          >
            <DesignHeader as={'h1'} size={'md'}>
              PROGRAMMING
            </DesignHeader>
          </Link>
        )}
        {partnerNetworkAccess && (
          <Link
            to='/partner-network'
            className={style.navigationLink}
            onClick={() => close()}
          >
            <DesignHeader as={'h1'} size={'md'}>
              PARTNER NETWORK
            </DesignHeader>
          </Link>
        )}
        {roundtablesAccess && (
          <Link
            to='/roundtables'
            className={style.navigationLink}
            onClick={() => close()}
          >
            <DesignHeader as={'h1'} size={'md'}>
              ROUNDTABLES
            </DesignHeader>
          </Link>
        )}
        {(playbookAccess || starterKitAccess || marketingAccess) && (
          <div
            className={classNames(
              style.businessResources,
              isBusinessOpen && style.businessOpen,
            )}
          >
            <div className={style.businessResourcesHeader}>
              <DesignHeader as={'h1'} size={'md'}>
                BUSINESS RESOURCES
              </DesignHeader>
              <button onClick={() => setIsBusinessOpen(!isBusinessOpen)}>
                {isBusinessOpen ? (
                  <Text
                    type='body'
                    size='xl'
                    bold
                    className={style.businessResourcesIcon}
                  >
                    -
                  </Text>
                ) : (
                  <Icon name='plus' className={style.businessResourcesIcon} />
                )}
              </button>
            </div>

            {isBusinessOpen && (
              <div className={style.businessLinks}>
                {playbookAccess && (
                  <div className={style.businessLink}>
                    <Link to='/playbook' onClick={close}>
                      <Text
                        type='body'
                        size={'sm'}
                        className={style.businessLinkText}
                      >
                        Playbook
                      </Text>
                    </Link>
                  </div>
                )}
                {starterKitAccess && (
                  <div className={style.businessLink} onClick={close}>
                    <Link to='/starter-kit'>
                      <Text
                        type={'body'}
                        size={'sm'}
                        className={style.businessLinkText}
                      >
                        Starter Kit
                      </Text>
                    </Link>
                  </div>
                )}
                {marketingAccess && (
                  <div className={style.businessLink}>
                    <Link to='/marketing' onClick={close}>
                      <Text
                        type={'body'}
                        size={'sm'}
                        className={style.businessLinkText}
                      >
                        Marketing
                      </Text>
                    </Link>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        <Link to='https://crossfit.com'>
          <div className={style.navigationMainsiteLink}>
            <Text className={style.goTo} type={'data'} size={'sm'}>
              Go To
            </Text>
            <Text className={style.goToLink} type={'data'} size={'sm'}>
              crossfit.com
            </Text>
            <img src={ExternalLink} className={style.linkIcon} />
          </div>
        </Link>
      </div>
    </div>
  )
}

export default function Header() {
  const isLessThanTablet = useMediaQuery('(max-width: 768px)')
  const user = useSelector(getUser) as User
  const affiliate = useSelector(getAffiliate)
  const { myAffiliates, selectedAffiliate } = useSelector(getMyAffiliates)
  const { showBanner } = useSelector(getBanner)
  const subscriptions = useMyActiveSubscriptions()
  const [isUserMenuExpanded, setIsUserMenuExpanded] = useState(false)
  const [isNavigationMenuExpanded, setIsNavigationMenuExpanded] =
    useState(false)
  const userDetail = useQuery(
    'userDetail',
    () => userService.fetchUserDetail(user?.id),
    {
      enabled: !!user?.id,
    },
  )
  const affiliateSelectorStyles: StylesConfig = {
    control: (styles) => ({
      ...styles,
      backgroundColor: '#1a1a1a',
      borderColor: '#333',
      maxWidth: '175px',
      color: 'white',
    }),
    option: (styles, { isSelected }) => {
      return {
        ...styles,
        backgroundColor: isSelected ? '#333' : '#1A1A1A',
        minHeight: '60px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        color: 'white',
        paddingLeft: isLessThanTablet ? '16px' : '8px',
        paddingTop: '10px',
        paddingRight: '10px',
        width: isLessThanTablet ? 'unset' : '200px',
      }
    },
    menuList: () => {
      return {
        padding: '0px',
      }
    },
    menu: (styles) => {
      return {
        ...styles,
        position: isLessThanTablet ? 'fixed' : 'absolute',
        top: isLessThanTablet ? '70px' : 'auto',
        left: isLessThanTablet ? '0' : 'auto',
        width: isLessThanTablet ? '100%' : 'auto',
        zIndex: isLessThanTablet ? '9999' : '1',
      }
    },
  }
  const formatAffiliateOptionLabel = (
    { value, label, customAbbreviation },
    { context },
  ) => {
    if (context === 'value') {
      return (
        <div className={style.affiliateSelectorContainerSelected}>
          <Text
            type={'body'}
            size={'sm'}
            className={style.affiliateSelectorOptionNameText}
          >
            {label}
          </Text>
        </div>
      )
    }
    return (
      <div className={style.affiliateSelectorContainer}>
        <div className={style.affiliateSelectorTextContainer}>
          <Text
            type={'overline'}
            size={'xs'}
            className={style.affiliateSelectorOptionRoleText}
          >
            {customAbbreviation}
          </Text>
          <Text
            type={'body'}
            size={'sm'}
            bold
            className={style.affiliateSelectorOptionNameText}
          >
            {label}
          </Text>
        </div>
        {affiliate?.id === value && (
          <Icon
            name={'check'}
            fontSize={12}
            className={style.affiliateSelectorCheck}
          />
        )}
      </div>
    )
  }
  const affiliateSelectorOptions = myAffiliates?.map((affiliate) => {
    return {
      value: affiliate.affiliateId,
      label: affiliate.affiliateName,
      customAbbreviation: affiliate.roles
        .filter((r) => [Role.LOR, Role.Coach, Role.Manager].includes(r as Role))
        .map((r) => (r === Role.LOR ? 'Owner' : r))[0],
    }
  })
  const isMobile = useMediaQuery(`(max-width: 1183px)`)
  const [isDropDownOpen, setIsDropDownOpen] = useState(false)
  const toggleUserMenu = () => {
    if (isUserMenuExpanded) {
      document.body.style.overflow = 'auto'
    } else {
      document.body.style.overflow = 'hidden'
      setIsNavigationMenuExpanded(false)
    }
    setIsUserMenuExpanded(!isUserMenuExpanded)
  }
  const dispatch = useDispatch() as AppDispatch
  const dashboardAccess = canAccessDashboard(selectedAffiliate, subscriptions)
  const programmingAccess = canAccessProgramming(
    selectedAffiliate,
    subscriptions,
  )
  const partnerNetworkAccess = canAccessPartnerNetwork(
    selectedAffiliate,
    subscriptions,
  )

  const roundtablesAccess = canAccessRoundtables(
    selectedAffiliate,
    subscriptions,
  )

  const playbookAccess = canAccessPlaybook(selectedAffiliate, subscriptions)

  const starterKitAccess = canAccessAffiliateStarterKit(
    selectedAffiliate,
    subscriptions,
  )

  const marketingAccess = canAccessContentKit(selectedAffiliate, subscriptions)

  return (
    <div className={style.wrapper}>
      {showBanner && (
        <NotificationBanner className={classNames(style.showBanner)} />
      )}
      <div className={style.header}>
        <div className={style.logoContainer}>
          {isMobile && (
            <img src={logoSmall} alt='CrossFit' className={style.logo} />
          )}
          {!isMobile && (
            <img src={logoMedium} alt='CrossFit' className={style.logo} />
          )}
        </div>
        <div className={style.actionBars}>
          <div className={style.top}>
            {isMobile && (
              <div className={style.left}>
                <Select
                  options={affiliateSelectorOptions}
                  styles={affiliateSelectorStyles}
                  formatOptionLabel={formatAffiliateOptionLabel}
                  onChange={(currentValue: {
                    value: any
                    label: string
                    customAbbreviation: string
                  }) => {
                    const affiliateId = Number(currentValue.value)
                    const newlySelectedAffiliate = myAffiliates.find(
                      (a) => a.affiliateId === affiliateId,
                    )
                    dispatch(selectAffiliateThunk(newlySelectedAffiliate))
                  }}
                  value={affiliateSelectorOptions?.find(
                    (o) => o.value === affiliate.id,
                  )}
                  components={{ IndicatorSeparator: () => null }}
                />
              </div>
            )}

            {isMobile && (
              <div className={style.actions}>
                <AccountButton
                  isMenuOpen={isUserMenuExpanded}
                  isLoggedIn
                  onLogin={() => {
                    return {}
                  }}
                  onClick={() => toggleUserMenu()}
                  profileImage={userDetail.data?.profileImageUrl}
                  firstChar={user?.firstName?.charAt(0)}
                />

                <button
                  className={style.menuButton}
                  onClick={() => {
                    if (!isNavigationMenuExpanded) {
                      document.body.style.overflow = 'hidden'
                    } else {
                      document.body.style.overflow = 'auto'
                    }
                    setIsNavigationMenuExpanded(!isNavigationMenuExpanded)
                  }}
                >
                  <Icon
                    name={isNavigationMenuExpanded ? 'close' : 'hamburger'}
                    className={style.menuIcon}
                  />
                </button>
              </div>
            )}
            {!isMobile && (
              <Link to='https://crossfit.com'>
                <div className={style.mainsiteLink}>
                  <Text className={style.goTo} type={'data'} size={'sm'}>
                    Go To
                  </Text>
                  <Text className={style.goToLink} type={'data'} size={'sm'}>
                    crossfit.com
                  </Text>
                  <img src={ExternalLink} className={style.linkIcon} />
                </div>
              </Link>
            )}
          </div>
          {!isMobile && (
            <div className={style.bottom}>
              <div className={style.bottomLinks}>
                {dashboardAccess && (
                  <Link to='/'>
                    <Text
                      type='button'
                      size='md'
                      className={style.mainLink}
                      bold
                    >
                      DASHBOARD
                    </Text>
                  </Link>
                )}
                {programmingAccess && (
                  <Link to='/programming'>
                    <Text
                      type='button'
                      size='md'
                      className={style.mainLink}
                      bold
                    >
                      PROGRAMMING
                    </Text>
                  </Link>
                )}
                {partnerNetworkAccess && (
                  <Link to='/partner-network'>
                    <Text
                      type='button'
                      size='md'
                      className={style.mainLink}
                      bold
                    >
                      PARTNER NETWORK
                    </Text>
                  </Link>
                )}
                {roundtablesAccess && (
                  <Link to='/roundtables'>
                    <Text
                      type='button'
                      size='md'
                      className={style.mainLink}
                      bold
                    >
                      ROUNDTABLES
                    </Text>
                  </Link>
                )}
                {(playbookAccess || starterKitAccess || marketingAccess) && (
                  <div className={style.dropDown}>
                    <button
                      className={style.dropButton}
                      onClick={() => setIsDropDownOpen(!isDropDownOpen)}
                    >
                      <Text
                        type='button'
                        size='md'
                        bold
                        className={style.mainLink}
                      >
                        BUSINESS RESOURCES
                      </Text>
                      <Icon name='chevron' className={style.icon} />
                    </button>
                    {isDropDownOpen && (
                      <div
                        className={classnames(style.dropDownContent, {
                          [style.visible]: isDropDownOpen,
                        })}
                      >
                        {playbookAccess && (
                          <Link
                            to='/playbook'
                            onClick={() => setIsDropDownOpen(false)}
                          >
                            <div className={style.dropDownLink}>
                              <Text
                                type={'body'}
                                size={'sm'}
                                className={style.dropDownLinkText}
                              >
                                Playbook
                              </Text>
                              <Icon
                                name='arrow-right'
                                className={style.dropDownLinkIcon}
                              />
                            </div>
                          </Link>
                        )}
                        {starterKitAccess && (
                          <Link
                            to='/starter-kit'
                            onClick={() => setIsDropDownOpen(false)}
                          >
                            <div className={style.dropDownLink}>
                              <Text
                                type={'body'}
                                size={'sm'}
                                className={style.dropDownLinkText}
                              >
                                Starter Kit
                              </Text>
                              <Icon
                                name='arrow-right'
                                className={style.dropDownLinkIcon}
                              />
                            </div>
                          </Link>
                        )}
                        {marketingAccess && (
                          <Link
                            to='/marketing'
                            onClick={() => setIsDropDownOpen(false)}
                          >
                            <div className={style.dropDownLink}>
                              <Text
                                type={'body'}
                                size={'sm'}
                                className={style.dropDownLinkText}
                              >
                                Marketing
                              </Text>
                              <Icon
                                name='arrow-right'
                                className={style.dropDownLinkIcon}
                              />
                            </div>
                          </Link>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
              {!isMobile && (
                <div className={style.actions}>
                  <Select
                    options={affiliateSelectorOptions}
                    styles={affiliateSelectorStyles}
                    formatOptionLabel={formatAffiliateOptionLabel}
                    onChange={(currentValue: {
                      value: any
                      label: string
                      customAbbreviation: string
                    }) => {
                      const affiliateId = Number(currentValue.value)
                      const newlySelectedAffiliate = myAffiliates.find(
                        (a) => a.affiliateId === affiliateId,
                      )
                      dispatch(selectAffiliateThunk(newlySelectedAffiliate))
                    }}
                    value={affiliateSelectorOptions?.find(
                      (o) => o.value === affiliate.id,
                    )}
                    components={{ IndicatorSeparator: () => null }}
                  />
                  <AccountButton
                    isMenuOpen={isUserMenuExpanded}
                    isLoggedIn
                    onLogin={() => {
                      return {}
                    }}
                    onClick={() => toggleUserMenu()}
                    profileImage={userDetail.data?.profileImageUrl}
                    firstChar={user?.firstName?.charAt(0)}
                  />
                  <div
                    className={classnames(
                      style.dropDownContent,
                      style.userDropDown,
                      {
                        [style.visible]: isUserMenuExpanded,
                      },
                    )}
                  >
                    <AccountMenu
                      onClose={toggleUserMenu}
                      isLoggedIn
                      loginButtonText='Login'
                      logoutText='Logout'
                      isMobile={isMobile}
                      viewProfileText={'View Profile'}
                    />
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
        {isMobile && isNavigationMenuExpanded && (
          <NavigationMenu
            close={() => {
              document.body.style.overflow = 'visible'
              setIsNavigationMenuExpanded(false)
            }}
          />
        )}
        {isMobile && isUserMenuExpanded && !isNavigationMenuExpanded && (
          <AccountMenu
            onClose={() => setIsUserMenuExpanded(false)}
            isLoggedIn
            loginButtonText='Login'
            logoutText='Logout'
            isMobile={isMobile}
            viewProfileText={'View Profile'}
          />
        )}
      </div>
    </div>
  )
}
